"use client";

import { PRIVACY_POLICY_URL } from "@/lib/cookie-consent";
import { useTranslations } from "next-intl";
import type { CSSProperties, ReactElement } from "react";

const ACCENT = "#008070";

export type PrivacyConsentVariant = "dark" | "light";

type PrivacyConsentFieldProps = {
  readonly id: string;
  readonly checked: boolean;
  readonly onCheckedChange: (checked: boolean) => void;
  readonly variant: PrivacyConsentVariant;
  readonly showError?: boolean;
};

type VariantTokens = {
  readonly labelColor: string;
  readonly linkColor: string;
  readonly boxBorder: string;
  readonly boxBorderChecked: string;
  readonly boxBackground: string;
  readonly boxBackgroundChecked: string;
  readonly errorColor: string;
};

const VARIANT_TOKENS: Record<PrivacyConsentVariant, VariantTokens> = {
  dark: {
    labelColor: "rgba(255,255,255,0.65)",
    linkColor: "rgba(255,255,255,0.9)",
    boxBorder: "rgba(255,255,255,0.22)",
    boxBorderChecked: ACCENT,
    boxBackground: "rgba(255,255,255,0.04)",
    boxBackgroundChecked: ACCENT,
    errorColor: "#ff6b6b",
  },
  light: {
    labelColor: "rgba(11,15,25,0.62)",
    linkColor: ACCENT,
    boxBorder: "rgba(11,15,25,0.18)",
    boxBorderChecked: ACCENT,
    boxBackground: "#fbfaf6",
    boxBackgroundChecked: ACCENT,
    errorColor: "#B24A30",
  },
};

function CheckIcon(): ReactElement {
  return (
    <svg width="11" height="11" viewBox="0 0 11 11" fill="none" aria-hidden>
      <path
        d="M2 5.5l2.5 2.5 4.5-5"
        stroke="#fff"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Required privacy-policy consent checkbox for public lead forms. */
export function PrivacyConsentField({
  id,
  checked,
  onCheckedChange,
  variant,
  showError = false,
}: PrivacyConsentFieldProps): ReactElement {
  const t = useTranslations("public.privacyConsentField");
  const tokens = VARIANT_TOKENS[variant];

  const boxStyle: CSSProperties = {
    width: 20,
    height: 20,
    flexShrink: 0,
    marginTop: 1,
    borderRadius: 6,
    border: `1.5px solid ${checked ? tokens.boxBorderChecked : tokens.boxBorder}`,
    background: checked ? tokens.boxBackgroundChecked : tokens.boxBackground,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "border-color .15s, background .15s, box-shadow .15s",
    boxShadow: checked ? "0 0 0 3px rgba(0,128,112,0.14)" : undefined,
  };

  return (
    <div style={{ width: "100%" }}>
      <label
        htmlFor={id}
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 12,
          cursor: "pointer",
          userSelect: "none",
          WebkitTapHighlightColor: "transparent",
          padding: "4px 0",
        }}
      >
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={(e) => onCheckedChange(e.target.checked)}
          style={{
            position: "absolute",
            width: 1,
            height: 1,
            padding: 0,
            margin: -1,
            overflow: "hidden",
            clip: "rect(0,0,0,0)",
            whiteSpace: "nowrap",
            border: 0,
          }}
        />
        <span aria-hidden style={boxStyle}>
          {checked ? <CheckIcon /> : null}
        </span>
        <span
          style={{
            fontSize: 13,
            lineHeight: 1.5,
            color: tokens.labelColor,
            textAlign: "left",
          }}
        >
          {t.rich("stimmeDatenschutzerklaerungZu", {
            link: (chunks) => (
              <a
                href={PRIVACY_POLICY_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{
                  color: tokens.linkColor,
                  textDecoration: "underline",
                  textUnderlineOffset: 2,
                }}
              >
                {chunks}
              </a>
            ),
          })}
        </span>
      </label>
      {showError && !checked ? (
        <div
          style={{
            fontSize: 12,
            color: tokens.errorColor,
            marginTop: 6,
            paddingLeft: 32,
          }}
        >
          {t("bitteStimmeDatenschutzerklaerung")}
        </div>
      ) : null}
    </div>
  );
}
